import React from "react";
import { withTranslation } from "react-i18next";
import "./CredentialsAndLicensingGlobalListing.scss";
import { v4 as uuidv4 } from "uuid";
import { CREDENTIAL_STATUS } from "../../constants";

/**
 * Documentation status API values.
 * All = 1, Proof Missing = 2, Completed = 3
 */
export const DOCUMENTATION_STATUS = {
  ALL: 1,
  PROOF_MISSING: 2,
  COMPLETED: 3,
};

/** Default: All selected ⇒ every Documentation Status option is checked. */
export const DEFAULT_DOCUMENTATION_STATUS = [
  DOCUMENTATION_STATUS.ALL,
  DOCUMENTATION_STATUS.PROOF_MISSING,
  DOCUMENTATION_STATUS.COMPLETED,
];

export const credentialStatus = [
  {
    id: CREDENTIAL_STATUS.CURRENT,
    name: "Current",
    translationKey: "accountOwner.currentStatus",
    customClass: "green",
    uniqueKey: uuidv4(),
  },
  {
    id: CREDENTIAL_STATUS.EXPIRING_SOON,
    name: "Expiring Soon",
    translationKey: "accountOwner.expiringSoon",
    customClass: "yellow",
    uniqueKey: uuidv4(),
  },
  {
    id: CREDENTIAL_STATUS.EXPIRED,
    name: "Expired",
    translationKey: "accountOwner.expired",
    customClass: "red",
    uniqueKey: uuidv4(),
  },
  {
    id: CREDENTIAL_STATUS.NO_EXPIRY,
    name: "No Expiry",
    translationKey: "accountOwner.noExpiryStatus",
    customClass: "blue",
    uniqueKey: uuidv4(),
  },
  {
    id: CREDENTIAL_STATUS.EXPIRY_UNKNOWN,
    name: "Expiry Unknown",
    translationKey: "accountOwner.expiryUnknownStatus",
    customClass: "dark-blue",
    uniqueKey: uuidv4(),
  },
];

/**
 * Resolve credential status display from API status id.
 * @param {{ status?: number }|null|undefined} row
 * @param {Function} [t] - optional i18n translate
 * @returns {{ status: string, customClass: string, showHide: boolean, id: number|null }}
 */
export function getDocumentStatus(row, t) {
  const statusId = row?.status != null ? +row.status : null;
  const matched = credentialStatus.find((item) => +item.id === statusId);

  if (!matched) {
    return {
      status: "",
      customClass: "",
      showHide: false,
      id: null,
    };
  }

  return {
    status:
      typeof t === "function" ? t(matched.translationKey) : matched.name,
    customClass: matched.customClass,
    showHide: true,
    id: matched.id,
  };
}
export const credentialType = [
  { id: 1, name: "License", uniqueKey: uuidv4() },
  { id: 2, name: "Certification", uniqueKey: uuidv4() },
  { id: 3, name: "Training", uniqueKey: uuidv4() },
];
/**
 * Documentation column / filter is based only on Proof Document presence.
 * Additional documents are ignored.
 * @param {{ proofDocumentPath?: string|null, documentationStatus?: number }} row
 * @returns {boolean}
 */
export function hasProofDocument(row) {
  if (row && Object.prototype.hasOwnProperty.call(row, "proofDocumentPath")) {
    return (
      typeof row.proofDocumentPath === "string" &&
      row.proofDocumentPath.trim().length > 0
    );
  }

  return +row?.documentationStatus === DOCUMENTATION_STATUS.COMPLETED;
}

export const documentationStatus = [
  { id: DOCUMENTATION_STATUS.ALL, name: "All", uniqueKey: uuidv4() },
  {
    id: DOCUMENTATION_STATUS.PROOF_MISSING,
    name: "Proof Missing",
    uniqueKey: uuidv4(),
  },
  {
    id: DOCUMENTATION_STATUS.COMPLETED,
    name: "Completed",
    uniqueKey: uuidv4(),
  },
];

/**
 * Normalize stored documentation status to an array (supports legacy single id).
 * @param {number|number[]|undefined|null} value
 * @returns {number[]}
 */
export const normalizeDocumentationStatus = (value) => {
  if (Array.isArray(value)) {
    if (value.length === 0) {
      return [...DEFAULT_DOCUMENTATION_STATUS];
    }
    // Legacy All-only (0 or only ALL) → select every option
    if (
      value.length === 1 &&
      (+value[0] === DOCUMENTATION_STATUS.ALL || +value[0] === 0)
    ) {
      return [...DEFAULT_DOCUMENTATION_STATUS];
    }
    return value;
  }
  if (typeof value === "number") {
    if (value === DOCUMENTATION_STATUS.ALL || value === 0) {
      return [...DEFAULT_DOCUMENTATION_STATUS];
    }
    return [value];
  }
  return [...DEFAULT_DOCUMENTATION_STATUS];
};

/**
 * Whether a non-default Documentation Status filter is applied.
 * @param {number[]} selectedValues
 * @returns {boolean}
 */
export const isDocumentationStatusFiltered = (selectedValues = []) => {
  const values = normalizeDocumentationStatus(selectedValues);
  return !values.includes(DOCUMENTATION_STATUS.ALL);
};

function CredentialTypeStatus({
  setSelectedValues,
  selectedValues = [],
  statusEnabled = false,
  documentationEnabled = false,
  t,
}) {
  const handleChange = (e, item) => {
    const offices = [...selectedValues];

    const oIndex = offices.findIndex((v) => +item.id === +v);
    if (e.target.checked) {
      offices.push(item.id);
    } else {
      if (oIndex > -1) offices.splice(oIndex, 1);
    }
    setSelectedValues(offices);
  };

  /**
   * Documentation Status multi-select:
   * - All selected ⇒ every option checked (default)
   * - Selecting both Proof Missing + Completed ⇒ auto-select All (all options)
   * - Clearing the last specific option restores All
   */
  const handleDocumentationChange = (e, item) => {
    if (typeof setSelectedValues !== "function") {
      return;
    }

    if (+item.id === DOCUMENTATION_STATUS.ALL) {
      if (e.target.checked) {
        setSelectedValues([...DEFAULT_DOCUMENTATION_STATUS]);
      } else {
        // Unchecking All clears every option; restore default All state
        setSelectedValues([...DEFAULT_DOCUMENTATION_STATUS]);
      }
      return;
    }

    let next = selectedValues.filter(
      (id) => +id !== DOCUMENTATION_STATUS.ALL,
    );

    if (e.target.checked) {
      if (!next.some((id) => +id === +item.id)) {
        next = [...next, item.id];
      }
    } else {
      next = next.filter((id) => +id !== +item.id);
    }

    const hasProofMissing = next.some(
      (id) => +id === DOCUMENTATION_STATUS.PROOF_MISSING,
    );
    const hasCompleted = next.some(
      (id) => +id === DOCUMENTATION_STATUS.COMPLETED,
    );

    if (hasProofMissing && hasCompleted) {
      setSelectedValues([...DEFAULT_DOCUMENTATION_STATUS]);
      return;
    }

    if (next.length === 0) {
      setSelectedValues([...DEFAULT_DOCUMENTATION_STATUS]);
      return;
    }

    setSelectedValues(next);
  };

  const statusTranslationMap = {
    Current: t("accountOwner.currentStatus"),
    "Expiring Soon": t("accountOwner.expiringSoon"),
    Expired: t("accountOwner.expired"),
    "No Expiry": t("accountOwner.noExpiryStatus"),
    "Expiry Unknown": t("accountOwner.expiryUnknownStatus"),
  };
  const typeTranslationMap = {
    License: t("accountOwner.license"),
    Certification: t("accountOwner.certification"),
    Training: t("accountOwner.training"),
  };
  const documentationTranslationMap = {
    All: t("all"),
    "Proof Missing": t("accountOwner.proofMissingStatus"),
    Completed: t("accountOwner.completedStatus"),
  };

  if (documentationEnabled) {
    return (
      <ul className={["filter-list"]}>
        {documentationStatus.map((item) => (
          <li key={item.uniqueKey}>
            <div className="ch-checkbox">
              <label>
                <input
                  type="checkbox"
                  onChange={(e) => handleDocumentationChange(e, item)}
                  checked={selectedValues?.some((id) => +id === +item.id)}
                />{" "}
                <span>
                  {documentationTranslationMap[item.name] || item.name}
                </span>
              </label>
            </div>
          </li>
        ))}
      </ul>
    );
  }

  const translationMap = statusEnabled
    ? statusTranslationMap
    : typeTranslationMap;

  return (
    <ul className={["filter-list"]}>
      {(statusEnabled ? credentialStatus : credentialType).map((item) => (
        <li key={item.uniqueKey}>
          <div className="ch-checkbox">
            <label>
              <input
                type="checkbox"
                onChange={(e) => handleChange(e, item)}
                checked={selectedValues?.some((id) => +id === +item.id)}
              />{" "}
              <span> {translationMap[item.name] || item.name}</span>
            </label>
          </div>
        </li>
      ))}
    </ul>
  );
}

export default withTranslation()(CredentialTypeStatus);
